<div class="page">
    <div class="page-head">
        <div>
            <h2>{{ title() }}</h2>
            <p>Products (master policies), their premium rules and benefit breakup — all in one place.</p>
        </div>
        <p-button label="Add Product" icon="pi pi-plus" (onClick)="openAddPolicy()" />
    </div>

    <!-- ===================== PRODUCTS ===================== -->
    <div class="card">
        <div class="toolbar">
            <p-iconfield iconPosition="left" class="search-field">
                <p-inputicon styleClass="pi pi-search" />
                <input type="text" pInputText placeholder="Search product, insurer or policy no..."
                    [ngModel]="policySearch()" (ngModelChange)="policySearch.set($event)">
            </p-iconfield>
            <span class="count">{{ filteredPolicies().length }} of {{ policies().length }}</span>
        </div>

        <p-table [value]="filteredPolicies()" [loading]="loadingPolicies()" dataKey="id" [rows]="10"
            [paginator]="filteredPolicies().length > 10" [rowsPerPageOptions]="[10, 25, 50]"
            styleClass="p-datatable-sm" selectionMode="single">
            <ng-template pTemplate="header">
                <tr>
                    <th>Product</th>
                    <th>Insurer</th>
                    <th>Policy No</th>
                    <th>Owner</th>
                    <th>Period</th>
                    <th>Status</th>
                    <th class="ta-r">Actions</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-p>
                <tr (click)="selectPolicy(p)" class="row-select" [class.row-active]="selectedPolicy()?.id === p.id">
                    <td class="name">{{ p.masterPolicyName }}</td>
                    <td>{{ p.insuranceCompanyName }}</td>
                    <td class="mono">{{ p.masterPolicyNumber }}</td>
                    <td>{{ ownerLabel(p) }}</td>
                    <td class="mono">{{ p.policyStartDate | indDate }} → {{ p.policyEndDate | indDate }}</td>
                    <td><p-tag [value]="p.status" [severity]="statusSeverity(p.status)" /></td>
                    <td class="ta-r actions" (click)="$event.stopPropagation()">
                        <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
                            pTooltip="Edit product" (onClick)="openEditPolicy(p)" />
                        <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
                            pTooltip="Delete product" (onClick)="removePolicy(p)" />
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="7">
                        <div class="state"><i class="pi pi-inbox"></i>
                            <p>No products yet. Click "Add Product" to create one.</p>
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>

    <!-- ===================== PREMIUM RULES OF THE SELECTED PRODUCT ===================== -->
    @if (selectedPolicy(); as sp) {
    <div class="card section">
        <div class="section-head">
            <div>
                <h3>Premium Rules — {{ sp.masterPolicyName }}</h3>
                <p class="hint-muted">Price bands for this product. Doctor Consultation is part of the price here.</p>
            </div>
            <p-button label="Add Rule" icon="pi pi-plus" (onClick)="openAddRule()" />
        </div>

        <p-table [value]="rules()" [loading]="loadingRules()" dataKey="id" [rows]="10"
            [paginator]="rules().length > 10" [rowsPerPageOptions]="[10, 25, 50]" styleClass="p-datatable-sm">
            <ng-template pTemplate="header">
                <tr>
                    <th>Code</th>
                    <th>Coverage</th>
                    <th>Age</th>
                    <th class="ta-r">Base</th>
                    <th class="ta-r">GST %</th>
                    <th class="ta-r">Gross</th>
                    <th class="ta-r">Doctor (incl. GST)</th>
                    <th>Status</th>
                    <th class="ta-r">Actions</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-r>
                <tr (click)="selectRule(r)" class="row-select" [class.row-active]="selectedRule()?.id === r.id">
                    <td class="mono">{{ r.premiumCode }}</td>
                    <td>{{ r.coverageType }}</td>
                    <td class="mono">{{ r.ageFrom }}–{{ r.ageTo }}</td>
                    <td class="ta-r">{{ r.basePremium }}</td>
                    <td class="ta-r">{{ r.gstPercentage }}</td>
                    <td class="ta-r strong">{{ grossOf(r) | number: '1.0-0' }}</td>
                    <td class="ta-r">
                        @if (doctorGrossOf(r) > 0) { {{ doctorGrossOf(r) | number: '1.0-0' }} }
                        @else { <span class="muted">—</span> }
                    </td>
                    <td><p-tag [value]="r.status" [severity]="statusSeverity(r.status)" /></td>
                    <td class="ta-r actions" (click)="$event.stopPropagation()">
                        <p-button icon="pi pi-list" [rounded]="true" [text]="true" severity="secondary"
                            pTooltip="Benefit breakup" (onClick)="selectRule(r)" />
                        <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
                            pTooltip="Edit rule" (onClick)="openEditRule(r)" />
                        <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
                            pTooltip="Delete rule" (onClick)="removeRule(r)" />
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="9">
                        <div class="state"><i class="pi pi-inbox"></i>
                            <p>No premium rules for this product yet. Click "Add Rule".</p>
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
    } @else {
    <div class="card section pick-hint">
        <i class="pi pi-arrow-up"></i>
        <p>Select a product above to see its premium rules and benefit breakup.</p>
    </div>
    }

    <!-- ===================== BENEFIT BREAKUP OF THE SELECTED RULE ===================== -->
    @if (selectedRule(); as sr) {
    <div class="card section">
        <div class="section-head">
            <div>
                <h3>Benefit Breakup — {{ sr.premiumCode }}</h3>
                <p class="hint-muted">
                    Benefit sums insured for this rule. Doctor Consultation is set on the rule
                    (price breakup), not here.
                </p>
            </div>
            <p-button label="Add Breakup" icon="pi pi-plus" (onClick)="openAddDetail()" />
        </div>

        <p-table [value]="details()" [loading]="detailsLoading()" dataKey="id" styleClass="p-datatable-sm">
            <ng-template pTemplate="header">
                <tr>
                    <th class="ta-r">Critical Illness</th>
                    <th class="ta-r">Daily Hosp.</th>
                    <th class="ta-r">ICU Hosp.</th>
                    <th class="ta-r">Accidental Death</th>
                    <th class="ta-r">Perm. Total Disab.</th>
                    <th class="ta-r">Perm. Partial Disab.</th>
                    <th class="ta-r">Convalescence</th>
                    <th class="ta-r">Doctor Consultation</th>
                    <th>Other</th>
                    <th>Active</th>
                    <th class="ta-r">Actions</th>
                </tr>
            </ng-template>
            <ng-template pTemplate="body" let-d>
                <tr>
                    <td class="ta-r">@if (d.criticalIllness != null) { {{ d.criticalIllness | number: '1.0-0' }} } @else { <span class="muted">—</span> }</td>
                    <td class="ta-r">@if (d.dailyHospitalisation != null) { {{ d.dailyHospitalisation | number: '1.0-0' }} } @else { <span class="muted">—</span> }</td>
                    <td class="ta-r">@if (d.icUhospitalization != null) { {{ d.icUhospitalization | number: '1.0-0' }} } @else { <span class="muted">—</span> }</td>
                    <td class="ta-r">@if (d.accidentalDeath != null) { {{ d.accidentalDeath | number: '1.0-0' }} } @else { <span class="muted">—</span> }</td>
                    <td class="ta-r">@if (d.permanentTotalDisablement != null) { {{ d.permanentTotalDisablement | number: '1.0-0' }} } @else { <span class="muted">—</span> }</td>
                    <td class="ta-r">@if (d.permanentPartialDisablement != null) { {{ d.permanentPartialDisablement | number: '1.0-0' }} } @else { <span class="muted">—</span> }</td>
                    <td class="ta-r">@if (d.convalescenceCover != null) { {{ d.convalescenceCover | number: '1.0-0' }} } @else { <span class="muted">—</span> }</td>
                    <td class="ta-r">
                        <!-- Mirrored from the rule; edit it on the rule's price form. -->
                        @if (d.doctorConsultant != null && d.doctorConsultant > 0) {
                            {{ d.doctorConsultant | number: '1.0-0' }}
                            <i class="pi pi-lock lock-ico" pTooltip="Set on the rule (price breakup)"></i>
                        } @else { <span class="muted">—</span> }
                    </td>
                    <td>
                        @if (d.other != null) {
                            <p-tag [value]="d.otherType || 'Other'" severity="info" />
                            <span class="ml">{{ d.other | number: '1.0-0' }}</span>
                        } @else { <span class="muted">—</span> }
                    </td>
                    <td><p-tag [value]="d.isActive ? 'ACTIVE' : 'INACTIVE'" [severity]="d.isActive ? 'success' : 'secondary'" /></td>
                    <td class="ta-r actions">
                        <p-button icon="pi pi-pencil" [rounded]="true" [text]="true" severity="secondary"
                            pTooltip="Edit" (onClick)="openEditDetail(d)" />
                        <p-button icon="pi pi-trash" [rounded]="true" [text]="true" severity="danger"
                            pTooltip="Delete" (onClick)="removeDetail(d)" />
                    </td>
                </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="11">
                        <div class="state"><i class="pi pi-inbox"></i>
                            <p>No benefit breakup yet for this rule. Click "Add Breakup".</p>
                        </div>
                    </td>
                </tr>
            </ng-template>
        </p-table>
    </div>
    }
</div>

<!-- ===================== ADD / EDIT PRODUCT ===================== -->
<p-dialog [visible]="showPolicyModal()" (visibleChange)="showPolicyModal.set($event)" [modal]="true" [draggable]="true"
    [resizable]="false" [closable]="true" [dismissableMask]="false"
    [header]="editingPolicy() ? 'Edit Product' : 'Add Product'" styleClass="menu-dialog"
    [style]="{ width: '640px' }" [breakpoints]="{ '640px': '94vw' }">
    <form [formGroup]="policyForm" class="menu-form">
        <div class="grid-2">
            <div class="field">
                <label>Product / Policy Name <span class="req">*</span></label>
                <input pInputText type="text" formControlName="masterPolicyName" placeholder="e.g. Poorna Suraksha – Group">
                @if (policyForm.get('masterPolicyName')?.touched && policyForm.get('masterPolicyName')?.invalid) {
                <small class="err">Name is required</small>
                }
            </div>
            <div class="field">
                <label>Insurance Company <span class="req">*</span></label>
                <input pInputText type="text" formControlName="insuranceCompanyName" placeholder="Insurer name">
                @if (policyForm.get('insuranceCompanyName')?.touched && policyForm.get('insuranceCompanyName')?.invalid) {
                <small class="err">Insurer is required</small>
                }
            </div>
        </div>

        <div class="grid-2">
            <div class="field">
                <label>Master Policy Number <span class="req">*</span></label>
                <input pInputText type="text" formControlName="masterPolicyNumber" placeholder="Policy number">
                @if (policyForm.get('masterPolicyNumber')?.touched && policyForm.get('masterPolicyNumber')?.invalid) {
                <small class="err">Policy number is required</small>
                }
            </div>
            <div class="field">
                <label>Currency <span class="req">*</span></label>
                <p-select formControlName="currency" [options]="currencies" appendTo="body" />
            </div>
        </div>

        <div class="grid-3">
            <div class="field">
                <label>Start Date <span class="req">*</span></label>
                <p-datepicker formControlName="policyStartDate" dateFormat="dd M yy" [showIcon]="true"
                    [iconDisplay]="'input'" [readonlyInput]="true" appendTo="body" />
            </div>
            <div class="field">
                <label>Term (years) <span class="req">*</span></label>
                <input pInputText type="number" formControlName="years" min="1" step="1">
            </div>
            <div class="field">
                <label>End Date</label>
                <p-datepicker formControlName="policyEndDate" dateFormat="dd M yy" [showIcon]="true"
                    [iconDisplay]="'input'" [readonlyInput]="true" appendTo="body" placeholder="Auto" />
                <small class="hint-muted">Start + term − 1 day.</small>
            </div>
        </div>

        <div class="grid-3">
            <div class="field">
                <label>Series Suffix</label>
                <input pInputText type="text" formControlName="seriesSuffix" placeholder="e.g. PSG">
            </div>
            <div class="field">
                <label>Series Start <span class="req">*</span></label>
                <input pInputText type="number" formControlName="seriesStartNo" min="0" step="1">
            </div>
            <div class="field">
                <label>Series End <span class="req">*</span></label>
                <input pInputText type="number" formControlName="seriesEndNo" min="0" step="1">
                @if (policyForm.errors?.['seriesRange'] && policyForm.get('seriesEndNo')?.touched) {
                <small class="err">Must be ≥ series start</small>
                }
            </div>
        </div>

        <div class="grid-3">
            <div class="field">
                <label>Owner Type <span class="req">*</span></label>
                <p-select formControlName="ownerType" [options]="ownerTypeOptions" optionLabel="label"
                    optionValue="value" appendTo="body" (onChange)="onOwnerTypeChange()" />
            </div>
            <div class="field">
                <label>Owner <span class="req">*</span></label>
                <p-select formControlName="ownerId" [options]="ownerOptions()" optionLabel="label"
                    optionValue="value" [filter]="true" filterBy="label" placeholder="Select owner..." appendTo="body" />
                @if (policyForm.get('ownerId')?.touched && policyForm.get('ownerId')?.invalid) {
                <small class="err">Owner is required</small>
                }
            </div>
            <div class="field">
                <label>Status</label>
                <p-select formControlName="status" [options]="statuses" appendTo="body" />
            </div>
        </div>

        <div class="field doc-row">
            <label class="doc-label">
                <p-toggleswitch formControlName="docEnabled" />
                <span>DOC — attach the welcome letter (doctor consultation) with the COI</span>
            </label>
        </div>

        <div class="modal-actions">
            <p-button label="Cancel" [outlined]="true" (onClick)="closePolicyModal()" />
            <p-button label="Save" (onClick)="savePolicy()" [loading]="savingPolicy()" />
        </div>
    </form>
</p-dialog>

<!-- ===================== ADD / EDIT PREMIUM RULE ===================== -->
<p-dialog [visible]="showRuleModal()" (visibleChange)="showRuleModal.set($event)" [modal]="true" [draggable]="true"
    [resizable]="false" [closable]="true" [dismissableMask]="false"
    [header]="editingRule() ? 'Edit Premium Rule' : 'Add Premium Rule'" styleClass="menu-dialog"
    [style]="{ width: '620px' }" [breakpoints]="{ '620px': '94vw' }">
    <form [formGroup]="ruleForm" class="menu-form">
        <p class="hint-muted">
            Product: <strong>{{ selectedPolicy()?.masterPolicyName }}</strong>
        </p>

        <div class="grid-2">
            <div class="field">
                <label>Coverage Type <span class="req">*</span></label>
                <input pInputText type="text" formControlName="coverageType" placeholder="Auto-filled from product">
                <small class="hint-muted">Auto-filled with the product name — change if needed.</small>
                @if (ruleForm.get('coverageType')?.touched && ruleForm.get('coverageType')?.invalid) {
                <small class="err">Coverage type is required</small>
                }
            </div>
            <div class="field">
                <label>Premium Code <span class="req">*</span></label>
                <input pInputText type="text" formControlName="premiumCode" placeholder="PR-IND-18-25">
                @if (ruleForm.get('premiumCode')?.touched && ruleForm.get('premiumCode')?.invalid) {
                <small class="err">Premium code is required</small>
                }
            </div>
        </div>

        <div class="grid-3">
            <div class="field">
                <label>Age From <span class="req">*</span></label>
                <input pInputText type="number" formControlName="ageFrom" min="0">
            </div>
            <div class="field">
                <label>Age To <span class="req">*</span></label>
                <input pInputText type="number" formControlName="ageTo" min="0">
                @if (ruleForm.errors?.['ageRange'] && ruleForm.get('ageTo')?.touched) {
                <small class="err">Must be ≥ Age From</small>
                }
            </div>
            <div class="field">
                <label>Status</label>
                <p-select formControlName="status" [options]="statuses" placeholder="Select status" appendTo="body" />
            </div>
        </div>

        <div class="grid-3">
            <div class="field">
                <label>GST % <span class="req">*</span></label>
                <input pInputText type="number" formControlName="gstPercentage" min="0" max="100" step="0.01"
                    (input)="onRuleAmountChange()">
                @if (ruleForm.get('gstPercentage')?.touched && ruleForm.get('gstPercentage')?.invalid) {
                <small class="err">Must be between 0 and 100</small>
                }
            </div>
            <div class="field">
                <label>Gross Premium <span class="req">*</span></label>
                <input pInputText type="number" formControlName="grossPremium" min="0" step="0.01"
                    (input)="onRuleAmountChange()">
                <small class="hint-muted">Entire plan amount (incl. GST).</small>
            </div>
            <div class="field">
                <label>Base Premium</label>
                <input pInputText type="number" formControlName="basePremium" min="0" step="0.01" readonly>
                <small class="hint-muted">Auto-calculated from Gross &amp; GST%.</small>
            </div>
        </div>

        <div class="grid-3">
            <div class="field">
                <label>Doctor Consultation</label>
                <input pInputText type="number" formControlName="doctorGross" min="0" step="0.01"
                    (input)="onRuleAmountChange()">
                <small class="hint-muted">Entire amount (incl. GST). 0 = no add-on.</small>
            </div>
            <div class="field">
                <label>Doctor Fee (pre-GST)</label>
                <input pInputText type="number" formControlName="doctorConsultant" readonly>
                <small class="hint-muted">Auto-calculated — added to base before GST.</small>
            </div>
            <div class="field">
                <label>Policy Term (years) <span class="req">*</span></label>
                <input pInputText type="number" formControlName="policyTermYears" min="1" step="1">
                <small class="hint-muted">Sets the COI period.</small>
            </div>
        </div>

        <!-- live price breakup — same maths the server uses when a member opts into the doctor add-on -->
        <div class="calc-box">
            <div class="calc-item">
                <span>Base</span>
                <strong>{{ planBasePreview() | number: '1.0-2' }}</strong>
            </div>
            <i class="pi pi-plus sep"></i>
            <div class="calc-item">
                <span>Doctor (pre-GST)</span>
                <strong>{{ doctorBasePreview() | number: '1.0-2' }}</strong>
            </div>
            <i class="pi pi-plus sep"></i>
            <div class="calc-item">
                <span>GST ({{ ruleForm.get('gstPercentage')?.value || 0 }}%)</span>
                <strong>{{ gstPreview() | number: '1.0-0' }}</strong>
            </div>
            <i class="pi pi-equals sep"></i>
            <div class="calc-item total">
                <span>Total with Doctor</span>
                <strong>{{ totalPreview() | number: '1.0-0' }}</strong>
            </div>
        </div>

        <div class="grid-2">
            <div class="field">
                <label>Effective Date</label>
                <p-datepicker formControlName="effectiveDate" dateFormat="dd M yy" [showIcon]="true"
                    [iconDisplay]="'input'" [readonlyInput]="true" appendTo="body" placeholder="Defaults to today" />
                <small class="hint-muted">Leave blank to default to today.</small>
            </div>
            <div class="field">
                <label>End Date</label>
                <p-datepicker formControlName="endDate" dateFormat="dd M yy" [showIcon]="true" [iconDisplay]="'input'"
                    [readonlyInput]="true" appendTo="body" placeholder="No expiry" />
                <p-checkbox [binary]="true" [ngModel]="ruleForm.get('neverExpires')?.value"
                    [ngModelOptions]="{ standalone: true }"
                    (onChange)="toggleNeverExpires($any($event).checked)" label="Never expires" styleClass="check" />
            </div>
        </div>

        <div class="modal-actions">
            <p-button label="Cancel" [outlined]="true" (onClick)="closeRuleModal()" />
            <p-button label="Save" (onClick)="saveRule()" [loading]="savingRule()" />
        </div>
    </form>
</p-dialog>

<!-- ===================== ADD / EDIT BENEFIT BREAKUP ===================== -->
<p-dialog [visible]="showDetailForm()" (visibleChange)="showDetailForm.set($event)" [modal]="true"
    [draggable]="true" [resizable]="false" [closable]="true" [dismissableMask]="false"
    [header]="editingDetail() ? 'Edit Benefit Breakup' : 'Add Benefit Breakup'" styleClass="menu-dialog"
    [style]="{ width: '620px' }" [breakpoints]="{ '620px': '94vw' }">
    <form [formGroup]="detailForm" class="menu-form">
        <p class="hint-muted">
            Rule: <strong>{{ selectedRule()?.premiumCode }}</strong> — all benefit amounts are optional.
            Doctor Consultation is edited on the rule (price breakup), not here.
        </p>

        <div class="grid-3">
            <div class="field">
                <label>Critical Illness</label>
                <input pInputText type="number" formControlName="criticalIllness" min="0" step="0.01" placeholder="—">
            </div>
            <div class="field">
                <label>Daily Hospitalisation</label>
                <input pInputText type="number" formControlName="dailyHospitalisation" min="0" step="0.01" placeholder="—">
            </div>
            <div class="field">
                <label>ICU Hospitalization</label>
                <input pInputText type="number" formControlName="icUhospitalization" min="0" step="0.01" placeholder="—">
            </div>
        </div>

        <div class="grid-3">
            <div class="field">
                <label>Accidental Death</label>
                <input pInputText type="number" formControlName="accidentalDeath" min="0" step="0.01" placeholder="—">
            </div>
            <div class="field">
                <label>Permanent Total Disablement</label>
                <input pInputText type="number" formControlName="permanentTotalDisablement" min="0" step="0.01" placeholder="—">
            </div>
            <div class="field">
                <label>Permanent Partial Disablement</label>
                <input pInputText type="number" formControlName="permanentPartialDisablement" min="0" step="0.01" placeholder="—">
            </div>
        </div>

        <div class="grid-3">
            <div class="field">
                <label>Convalescence Cover</label>
                <input pInputText type="number" formControlName="convalescenceCover" min="0" step="0.01" placeholder="—">
                <small class="hint-muted">Paid per hospitalisation event, not per day.</small>
            </div>
            <div class="field">
                <label>Other Type</label>
                <input pInputText type="text" formControlName="otherType" placeholder="e.g. Maternity">
            </div>
            <div class="field">
                <label>Other Amount</label>
                <input pInputText type="number" formControlName="other" min="0" step="0.01" placeholder="—">
            </div>
        </div>

        <p-checkbox [binary]="true" formControlName="isActive" label="Active" styleClass="check" />
        <small class="hint-muted">Saving as Active automatically deactivates this rule's other breakup rows — the new one applies.</small>

        <div class="modal-actions">
            <p-button label="Cancel" [outlined]="true" (onClick)="closeDetailForm()" />
            <p-button label="Save" (onClick)="saveDetail()" [loading]="savingDetail()" />
        </div>
    </form>
</p-dialog>
